import {
  HOUSEHOLD_DOC,
  HOUSEHOLD_MEMBERS_KEY,
  type HouseholdMember,
  type MemberColor,
} from '@awwyeah/shared';
import { useEffect, useState } from 'react';
import { useYDoc, useYMap } from '../../../sync';
import { AuthApiError, AvatarPicker, ColorPicker, useAuth } from '../../auth';
import { Sheet } from './Sheet';

export interface ProfileEditSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Edit profile" (2026-08-01, usability testing: nobody could change name/color/emoji after
 * registration). Saves through `PATCH /api/auth/me` — the auth context updates the session
 * user — and then writes the same shape straight into the household Yjs doc's member map so
 * the change is live everywhere (roster, list attribution, calendar) without waiting on the
 * calendar module's `ensureSelfRegistered` backstop to run on next visit.
 */
export function ProfileEditSheet({ open, onClose }: ProfileEditSheetProps): JSX.Element | null {
  const { user, updateProfile } = useAuth();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { map: memberMap, entries: memberEntries } = useYMap<HouseholdMember>(
    doc,
    HOUSEHOLD_MEMBERS_KEY,
  );

  const [name, setName] = useState(user?.name ?? '');
  const [color, setColor] = useState<MemberColor | null>((user?.color as MemberColor) ?? null);
  const [avatarEmoji, setAvatarEmoji] = useState(user?.avatarEmoji ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset the form to the current session user each time the sheet opens, so a previous
  // edit-then-cancel doesn't leave stale values in place next time it's opened.
  useEffect(() => {
    if (!open || !user) return;
    setName(user.name);
    setColor(user.color as MemberColor);
    setAvatarEmoji(user.avatarEmoji ?? '');
    setError(null);
  }, [open, user]);

  const takenBy = memberEntries
    .map(([, member]) => member)
    .filter((member) => member.id !== user?.id)
    .map((member) => ({ color: member.color, name: member.name, avatarEmoji: member.avatarEmoji }));

  async function handleSave(): Promise<void> {
    if (!user) return;
    if (!color) {
      setError('Pick a colour.');
      return;
    }
    if (!name.trim()) {
      setError('Name can’t be empty.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const updated = await updateProfile({
        name: name.trim(),
        color,
        avatarEmoji: avatarEmoji || '👤',
      });
      // Same shape `ensureSelfRegistered` writes (apps/web/src/modules/calendar/useCalendarData.ts)
      // — write it here too so the roster/attribution/calendar colours update immediately
      // instead of waiting for that backstop to run on next calendar visit.
      const next: HouseholdMember = {
        id: updated.id,
        name: updated.name,
        color: updated.color,
        ...(updated.avatarEmoji ? { avatarEmoji: updated.avatarEmoji } : {}),
      };
      memberMap.set(updated.id, next);
      onClose();
    } catch (err) {
      setError(
        err instanceof AuthApiError && err.code === 'too_many_attempts'
          ? 'Too many changes at once — try again in a minute.'
          : 'Something went wrong saving your profile. Try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      height="tall"
      label="Edit profile"
      testId="profile-edit-sheet"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
        <h2 className="font-display text-lg text-fg">Edit your profile</h2>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
            data-testid="profile-edit-name"
          />
        </label>

        <div className="text-sm">
          <span className="mb-1 block text-fg-2">Colour</span>
          <ColorPicker value={color} onChange={setColor} takenBy={takenBy} />
        </div>

        <div className="text-sm">
          <span className="mb-1 block text-fg-2">Avatar</span>
          <AvatarPicker value={avatarEmoji} onChange={setAvatarEmoji} />
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] flex-1 rounded-md border border-line px-4 font-semibold text-fg-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="min-h-[44px] flex-1 rounded-md bg-inverse px-4 font-semibold text-inverse-fg disabled:opacity-50"
            data-testid="profile-edit-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
